import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { Box, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { CheckoutAccordion, CheckoutAccordionSummary, CheckoutAccordionActions } from 'shared/components';
import { DefaultInput } from 'shared/components/text-input-with-label';
import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import { useUpdateUser } from 'src/state/actions/users';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import errors from 'shared/core/fieldValidationErrorMessages';
import { isDutchiePlusKioskOrder } from 'shared/helpers/checkout';

function Additional() {
  const [open, setOpen] = useState(false);
  const [driversLicense, setDriversLicense] = useState('');
  const Checkout = useCheckout();
  const { Cart, User, UI } = useStores();
  const amplitude = useAmplitude();
  const updateUser = useUpdateUser();

  const isLoggedInUser = User.exists && !User.hasExternalUser;

  const isDutchiePlusKiosk = isDutchiePlusKioskOrder(Cart.orderType, UI.isPlus);

  function resetFields() {
    const licenseOnOrder = _.get(Cart, 'order.driversLicense', '');
    setDriversLicense(licenseOnOrder);
    if (_.isEmpty(licenseOnOrder)) {
      setOpen(true);
    }
  }

  // reset fields on mount and open if empty.
  useEffect(() => {
    resetFields();
  }, []);

  function onCancel() {
    resetFields();
    setOpen(!open);
  }

  function onSave() {
    amplitude.log(AmplitudeEvents.checkout.clickSave, {
      componentName: `Drivers License`,
      dispensaryId: Cart?.dispensary?.id,
      dispensaryName: Cart?.dispensary?.name,
    });
    if (driversLicense.length === 0) {
      Checkout.addErrors([{ path: 'driversLicense', message: errors.driversLicense.missing }]);
    } else {
      Cart.setDriversLicenseNumber(driversLicense);

      if (isLoggedInUser) {
        User.profile.driversLicense = driversLicense;

        updateUser(User);
      }

      setOpen(!open);
    }
  }

  const handleInputChange = (value) => {
    setDriversLicense(value);
    Cart.setDriversLicenseNumber(value);
  };

  return (
    <CheckoutLabelGrouping id='additional'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Additional Info</CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        <CheckoutAccordion
          expanded={open}
          dataCyName='additional'
          onExpand={onCancel}
          onCancel={onCancel}
          onSave={onSave}
          error={Checkout.getError({ path: 'driversLicense' }).error}
          defaultCancel={!!Cart.order.driversLicense}
          crossfade
        >
          <CheckoutAccordionSummary>
            <SummaryText fontSize={14} color='#5d666d' pt='2px'>
              {_.get(Cart, 'order.driversLicense', '').length > 0 && <b>Drivers License: </b>}
              {_.get(Cart, 'order.driversLicense', 'Please enter your drivers license number')}
            </SummaryText>
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <Box mt={!Cart.order.driversLicense ? '14px' : '24px'} mb='17px' width='100%'>
              <StyledDefaultInput
                id='driversLicenseNumber'
                data-cy='driver-license-field'
                data-test='driver-license-field'
                label='Drivers License Number'
                value={driversLicense}
                onChange={({ target }) => handleInputChange(target.value)}
                onValueAfterError={() => Checkout.removeError({ path: 'driversLicense' })}
                error={Checkout.getError({ path: 'driversLicense' }).error}
                errorMessage={Checkout.getError({ path: 'driversLicense' }).message}
                autoComplete={isDutchiePlusKiosk && 'off'}
                required
              />
            </Box>
          </CheckoutAccordionActions>
        </CheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(Additional);

const StyledDefaultInput = styled(DefaultInput)`
  width: 100%;
  border: 1px solid blue;
`;

const SummaryText = styled(Text)`
  span {
    font-weight: bold;
  }
`;
